/**
 * The items a bundle component may use (tracker item 85) and the catalog
 * total of a bundle for the settings list.
 */
import type { IItemsApi } from '../../api';
import * as types from '../../api/types';

const ELIGIBLE: string[] = [types.ItemType.SERVICE, types.ItemType.LABOR, types.ItemType.PRODUCT, types.ItemType.FEE];

/** Active, non-system SERVICE, LABOR, PRODUCT or FEE items (the backend rejects the rest). */
export function isBundleItem(item: types.Item): boolean {
  return ELIGIBLE.includes(item.itemType) && !item.isSystem;
}

/** Every live item, page by page (at most 20 pages of 50). */
export async function loadAllItems(api: Pick<IItemsApi, 'listItems'>): Promise<types.Item[]> {
  const items: types.Item[] = [];
  for (let page = 1; page <= 20; page += 1) {
    const result = await api.listItems(page, 50);
    items.push(...result.data);
    if (page >= result.pagination.pages) break;
  }
  return items;
}

export interface CatalogTotal {
  /** Sum of quantity x catalog price of the components priced from the catalog */
  amount: number;
  /** Some components are priced from a price chart (by property size), so the real total varies */
  sizePriced: boolean;
}

/** The catalog-priced total of a bundle; chart-priced components are flagged, not guessed. */
export function catalogTotal(components: { itemId: string; quantity: string }[], items: types.Item[]): CatalogTotal {
  let amount = 0;
  let sizePriced = false;
  components.forEach((c) => {
    const item = items.find((i) => i.id === c.itemId);
    if (!item) return;
    if (item.priceChartId) { sizePriced = true; return; }
    amount += Number(c.quantity) * Number(item.unitPrice);
  });
  return { amount, sizePriced };
}

export function catalogTotalText(total: CatalogTotal): string {
  const money = `$${total.amount.toFixed(2)}`;
  return total.sizePriced ? `${money} + size-priced items` : money;
}
